import * as React from 'react'
import {
  useNavigate as useBaseNavigate,
  useLocation,
  useParams,
  useBlocker,
  type BlockerFunction,
  NavigateFunction,
  NavigateOptions,
} from 'react-router-dom'
import type { To, Location } from 'history'

type WithRouterExtraProps<PARAMS> = {
  history: {
    push: (to: To) => void
  }
  location: Location
  match: {
    params: PARAMS
  }
}

export function withRouter<PARAMS, PROPS extends Partial<WithRouterExtraProps<PARAMS>>>(
  Component: React.ComponentType<PROPS>,
): React.ComponentType<PROPS> {
  ;(WithRouter as React.ComponentType<PROPS>).displayName = `WithRouter(${Component.displayName ?? '??'})`

  return WithRouter

  function WithRouter(props: PROPS) {
    const navigate = useNavigate()
    const location = useLocation()
    const params = useParams()

    return (
      <Component
        {...props}
        history={{
          push: (to) => {
            navigate(to)
          },
        }}
        location={location}
        match={{
          params,
        }}
      />
    )
  }
}

type PromptProps = {
  message: string
  when: boolean
}

export function Prompt({ message, when }: PromptProps) {
  useExtremelyUnstableAndDangerousPrompt(message, when)
  return null
}

export function useExtremelyUnstableAndDangerousPrompt(message: string, when = true) {
  const mountedRef = React.useRef<boolean>(false)

  React.useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const blocker = React.useCallback<BlockerFunction>(() => {
    if (!mountedRef.current) {
      return false
    }
    if (when) {
      return !window.confirm(message)
    }
    return false
  }, [message, when])

  useBlocker(blocker)
}

export function useNavigate(): NavigateFunction {
  const baseNavigate = useBaseNavigate()
  const [call, setCall] = React.useState<[To | number, NavigateOptions | undefined] | undefined>(undefined)

  // Navigating after multiple state update does not work because react-router
  // uses transitions that are canceled by other state updates. Instead we set
  // in a state the url to navigate and after the state has setled (so in the
  // useEffect), we navigate

  React.useEffect(() => {
    if (call !== undefined) {
      if (typeof call[0] === 'number') {
        baseNavigate(call[0])
      } else {
        baseNavigate(call[0], call[1])
      }
    }
  }, [call])

  return ((toOrDelta: To | number, options: NavigateOptions | undefined) => {
    if (typeof toOrDelta === 'number') {
      setCall([toOrDelta, undefined])
    } else {
      setCall([toOrDelta, options])
    }
  }) as NavigateFunction
}
